import i18n from "@/i18n";
import { FILE_TYPE_OTHER } from "@/constants/files";
import { GROUP_STATUS_PRECEDENCE } from "@/constants/documents";
import { renderCommentAsPlainText } from "@/lib/comments";
import { formatDocumentListFileSize } from "@/util/formatting";

import type { Document, RequiredDocument } from "@/api/http/schemas/documentsSchemas";
import type {
  DocumentFileRow,
  DocumentPendingRow,
  DocumentRow,
  DocumentStatus,
  PendingUpload,
} from "@/types/DocumentTypes";

const { t } = i18n.global;

type BuildDocumentRowsParams = {
  requiredDocuments: RequiredDocument[];
  uploadedDocuments: Document[];
  pendingUploads: PendingUpload[];
  processingDocumentIds: Set<Document["id"]>;
  uploadErrors: Record<Document["file_type"], string>;
  replaceErrors: Record<Document["id"], string>;
  isScopedToRequirements?: boolean;
};

type DeriveDocumentStatusParams = {
  document: Document;
  isProcessing: boolean;
};

type IsAwaitingCategorizationParams = {
  postedFileType?: Document["file_type"];
  returnedFileType: Document["file_type"];
};

type RollUpGroupStatusParams = {
  children: (DocumentFileRow | DocumentPendingRow)[];
  requirement: RequiredDocument;
};

type ToFileRowParams = {
  document: Document;
  processingDocumentIds: Set<Document["id"]>;
  replaceErrors: Record<Document["id"], string>;
  requirement?: RequiredDocument;
};

type RequiredDocumentsForCheckoutStepParams = {
  requiredDocuments: RequiredDocument[];
  stepId: string;
  hasOwnStips: boolean;
};

const STATUS_LABEL_KEYS = {
  awaiting_upload: "documents.statuses.awaitingUpload",
  uploading: "documents.statuses.uploading",
  detecting_type: "documents.statuses.detectingType",
  complete: "documents.statuses.complete",
  action_needed: "documents.statuses.actionNeeded",
} as const;

export const isUntypedFileType = (fileType?: Document["file_type"]) =>
  !fileType || fileType === FILE_TYPE_OTHER;

export const unresolvedComments = (document: Document) =>
  (document.comments ?? []).filter((comment) => !comment.is_resolved);

const withFileSize = (name: string, fileSizeInBytes: number) => {
  const formattedSize = formatDocumentListFileSize(fileSizeInBytes);

  return formattedSize ? `${name} • ${formattedSize}` : name;
};

export const documentRowDisplayName = (row: DocumentRow) => {
  if (row.kind === "file") {
    return withFileSize(row.document.original_name, row.document.file_size);
  }

  if (row.kind === "pending") {
    return withFileSize(row.pendingUpload.fileName, row.pendingUpload.fileSize);
  }

  return row.requirement.name;
};

export const documentStatusLabel = (status: DocumentStatus) => t(STATUS_LABEL_KEYS[status.status]);

export const isTypeUndetected = (status: DocumentStatus) =>
  status.status === "action_needed" && status.reason === "type_undetected";

const documentRowStatusMessages = (status: DocumentStatus) => {
  if (isTypeUndetected(status)) {
    return [t("documents.inline.typeUndetected")];
  }

  if (status.status === "action_needed" && status.reason === "validation_errors") {
    return status.validationErrors;
  }

  return [];
};

export const documentRowInlineMessages = (row: DocumentRow) => {
  const uploadErrors = row.uploadError ? [row.uploadError] : [];

  if (row.kind === "requirement" || row.kind === "group") {
    return uploadErrors;
  }

  return [...uploadErrors, ...documentRowStatusMessages(row.status)];
};

export const requiredDocumentsForCheckoutStep = ({
  requiredDocuments,
  stepId,
  hasOwnStips,
}: RequiredDocumentsForCheckoutStepParams) => {
  if (!hasOwnStips) {
    return requiredDocuments.filter((requirement) => !requirement.checkout_step_ids.length);
  }

  return requiredDocuments.filter((requirement) => requirement.checkout_step_ids.includes(stepId));
};

export const deriveDocumentStatus = ({
  document,
  isProcessing,
}: DeriveDocumentStatusParams): DocumentStatus => {
  if (isProcessing) {
    return { status: "detecting_type" };
  }

  const commentsNeedingResolution = unresolvedComments(document);

  if (commentsNeedingResolution.length) {
    return {
      status: "action_needed",
      reason: "validation_errors",
      validationErrors: commentsNeedingResolution.map(renderCommentAsPlainText),
    };
  }

  if (document.file_type === FILE_TYPE_OTHER) {
    return { status: "action_needed", reason: "type_undetected" };
  }

  return { status: "complete" };
};

// The API only broadcasts file_type_changed when categorization changes the type, so a typed upload may never get one.
export const isAwaitingCategorization = ({
  postedFileType,
  returnedFileType,
}: IsAwaitingCategorizationParams) => {
  return isUntypedFileType(postedFileType) && returnedFileType === FILE_TYPE_OTHER;
};

export const countDocumentsNeedingAction = (rows: DocumentRow[]) =>
  rows
    .flatMap((row) => (row.kind === "group" ? row.children : [row]))
    .filter((row) => row.kind === "file" && row.status.status === "action_needed").length;

const rollUpGroupStatus = ({ children, requirement }: RollUpGroupStatusParams): DocumentStatus => {
  for (const candidate of GROUP_STATUS_PRECEDENCE) {
    const match = children.find((child) => child.status.status === candidate);

    if (!match) {
      continue;
    }

    if (candidate === "complete" && requirement.uploaded_count < requirement.required_count) {
      return { status: "awaiting_upload" };
    }

    return match.status;
  }

  return { status: "awaiting_upload" };
};

const toPendingRow = (
  pendingUpload: PendingUpload,
  requirement?: RequiredDocument
): DocumentPendingRow => ({
  kind: "pending",
  key: `pending-${pendingUpload.id}`,
  pendingUpload,
  requirement,
  status: { status: "uploading" },
});

const toFileRow = ({
  document,
  processingDocumentIds,
  replaceErrors,
  requirement,
}: ToFileRowParams): DocumentFileRow => ({
  kind: "file",
  key: `file-${document.id}`,
  document,
  requirement,
  status: deriveDocumentStatus({
    document,
    isProcessing: processingDocumentIds.has(document.id),
  }),
  uploadError: replaceErrors[document.id],
});

export const buildDocumentRows = ({
  requiredDocuments,
  uploadedDocuments,
  pendingUploads,
  processingDocumentIds,
  uploadErrors,
  replaceErrors,
  isScopedToRequirements = false,
}: BuildDocumentRowsParams) => {
  const claimedDocumentIds = new Set<Document["id"]>();
  const claimedPendingUploadIds = new Set<PendingUpload["id"]>();
  const rows: DocumentRow[] = [];

  for (const requirement of requiredDocuments) {
    const files = uploadedDocuments.filter(
      (document) => document.file_type === requirement.file_type
    );
    const pending = pendingUploads.filter(
      (pendingUpload) => pendingUpload.fileType === requirement.file_type
    );

    for (const file of files) {
      claimedDocumentIds.add(file.id);
    }

    for (const pendingUpload of pending) {
      claimedPendingUploadIds.add(pendingUpload.id);
    }

    const children: (DocumentFileRow | DocumentPendingRow)[] = [
      ...pending.map((pendingUpload) => toPendingRow(pendingUpload, requirement)),
      ...files.map((document) =>
        toFileRow({ document, processingDocumentIds, replaceErrors, requirement })
      ),
    ];

    const uploadError = uploadErrors[requirement.file_type];

    if (!children.length && requirement.required_count <= 1) {
      rows.push({
        kind: "requirement",
        key: `requirement-${requirement.id}`,
        requirement,
        status: { status: "awaiting_upload" },
        uploadError,
      });
      continue;
    }

    if (requirement.required_count === 1 && children.length === 1) {
      rows.push({
        ...children[0],
        key: `requirement-${requirement.id}`,
        uploadError: uploadError ?? children[0].uploadError,
      });
      continue;
    }

    rows.push({
      kind: "group",
      key: `group-${requirement.id}`,
      requirement,
      children,
      status: rollUpGroupStatus({ children, requirement }),
      uploadError,
    });
  }

  for (const document of uploadedDocuments) {
    if (claimedDocumentIds.has(document.id)) {
      continue;
    }

    // A typed file outside these requirements belongs to another step; an untyped one has no home yet.
    if (isScopedToRequirements && !isUntypedFileType(document.file_type)) {
      continue;
    }

    rows.push(toFileRow({ document, processingDocumentIds, replaceErrors }));
  }

  for (const pendingUpload of pendingUploads) {
    if (claimedPendingUploadIds.has(pendingUpload.id)) {
      continue;
    }

    if (isScopedToRequirements && !isUntypedFileType(pendingUpload.fileType)) {
      continue;
    }

    rows.push(toPendingRow(pendingUpload));
  }

  return rows;
};
